import p from 'prop-types';
import React from 'react';
import styled from 'styled-components';

const ErrorMessage = styled.p`
  with: 100%;
  text-align: center;
  opacity: 0.6;
`;

/**
 *  Error Boundary component used to catch UI breaking errors in its children and display a default or custom component/message.
 */
class ErrorBoundary extends React.Component {
  state = { caughtError: false, showError: false };

  static getDerivedStateFromError = (error) => {
    console.log(error);
    // Update state so the next render will show the fallback UI.
    return { caughtError: true };
  };
  static getDerivedStateFromProps = (props, _state) => {
    if (props.isError) {
      return { showError: true };
    } else {
      return { showError: false };
    }
  };
  componentDidCatch = (error, errorInfo) => {
    // You can also log the error to an error reporting service
    this.props.onError(error, errorInfo);
  };
  render() {
    // This must be exactly one element or it will warn.
    const Children = this.props.children;
    if (this.state.caughtError || this.state.showError) {
      // You can render any custom fallback UI
      return this.props.errorComponent ?? <ErrorMessage>{this.props.errorMessage}</ErrorMessage>;
    }
    if (typeof Children === 'function') {
      return <Children />;
    }
    return Children;
  }
}

ErrorBoundary.propTypes = {
  /** Use this if you also want to control the state of the component. This allows you to trigger the error message programatically, not just when errors are thrown in its children. */
  isError: p.bool,
  /** Custom message to display when an error happens. */
  errorMessage: p.oneOfType([p.string, p.node]),
  /** Custom component to display when an error happens. */
  errorComponent: p.element,
  /** This function will be called with error details. */
  onError: p.func,
  children: p.oneOfType([p.element, p.func]).isRequired,
};

ErrorBoundary.defaultProps = {
  errorMessage: 'Something went wrong.',
  onError: () => {},
};

export default ErrorBoundary;
